import { Injectable } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep } from 'lodash';
import { GroupType } from 'src/AngularJS/Models/group-type.model';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { LoginWTO, User } from '../../../admin/user/user-admin.model';
import { ObjectiveLandingDataObj, ObjectivePalette, ObjectivesSaveObject, ObjectiveAssignmentDataObj } from './cxone-objectives.model';
import { MetricDefinition } from 'src/shared/models/pipeline-config/metric-definition.models';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';

export interface GroupTypes {
    emp: GroupType[];
    rec: GroupType[];
}

@Injectable({
    providedIn: 'root'
})
export class CxoneObjectivesService {
    public objectiveLocalization: Map<string, string>;
    public popoverObj: NgbPopover;

    constructor(
        private cvApiService: CVApiService,
        private cvLocalizationFactory: CVLocalizationService
    ) {
    }

    public async loadObjectives() {
        try {
            let response = await this.cvApiService.get<ObjectiveLandingDataObj>(`/api/v1/objective_load_Objectives/false`);
            if (response.status_code >= 200 && response.status_code < 300) {
                // Get percent metricIds
                let percentMetricIds = new Set<number>();
                let metricKeys = Object.keys(response.data.metricAbrDict)
                for (let key of metricKeys) {
                    let metric = response.data.metricAbrDict[key];
                    if (metric.NumericType == 2)
                        percentMetricIds.add(metric.MetricID);
                }

                for (let objective of response.data.assignmentData) {
                    for (let groupLocation of objective.assignments.emp) {
                        if (groupLocation.GroupID == 0) {
                            groupLocation.GroupName = this.getLocalizationString("All", "All") + " " + groupLocation.GroupName + " " + this.getLocalizationString("Groups", "Groups");
                        }
                    }
                    for (let groupLocation of objective.assignments.rec) {
                        if (groupLocation.GroupID == 0) {
                            groupLocation.GroupName = this.getLocalizationString("All", "All") + " " + groupLocation.GroupName + " " + this.getLocalizationString("Groups", "Groups");
                        }
                    }
                }

                return response.data;
            }
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load objectives.`);
        }
    }

    public async loadGroupTypes() {
        try {
            let response = await this.cvApiService.get<GroupTypes>(`/api/v2/pipeline/metric-definition/group-types`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load group types.`);
        }
    }

    public getPopover(popover: NgbPopover) {
        this.popoverObj = popover;
    }

    public async loadPalettes(){
        try {
            let response = await this.cvApiService.get<ObjectivePalette[]>(`/api/v1/objective_color_palettes/`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load objectives color palettes.`);
        }
    }

    public async loadObjectiveParts(objectiveId: number, metricObjectiveType: number, metricNumericType: number) {
        try {
            let response = await this.cvApiService.get<ObjectiveAssignmentDataObj>("/api/v1/objective_load_ObjectiveParts/" + objectiveId + "/" + metricObjectiveType);
            if (response.status_code >= 200 && response.status_code < 300) {
                // Convert percent objective detail values to whole numbers
                let objective = response.data

                if (metricNumericType == 2) {
                    for (let objectiveDetail of objective.combinedObj.ObjectiveDetails){
                        if (!!objectiveDetail.dayMinValue)
                            objectiveDetail.dayMinValue *= 100;
                        else if (!!objectiveDetail.weekMinValue)
                            objectiveDetail.weekMinValue *= 100;
                        else if (!!objectiveDetail.monthMinValue)
                            objectiveDetail.monthMinValue *= 100;
                    }
                }

                return response.data;
            }
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load objective parts.`);
        }
    }

    public async deactivateObjective(stringToRemove) {
        try {
            let response = await this.cvApiService.delete("/api/v1/objective_Remove_Objectives/" + stringToRemove);
            if (response.status_code >= 200 && response.status_code < 300)
                return;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to delete objectives.`);
        }
    }

    public async saveObjective(objectiveToSave: ObjectivesSaveObject, metricNumericType: number) {
        // Convert percent objective detail values from whole numbers to floating point
        if (metricNumericType == 2) {
            objectiveToSave.editedList = cloneDeep(objectiveToSave.editedList);
            for (var obj of objectiveToSave.editedList)
                for (var od of obj.ObjectiveDetails) {
                    if (od.dayMinValue != null)
                        od.dayMinValue /= 100;
                    else if (od.weekMinValue != null)
                        od.weekMinValue /= 100;
                    else if (od.monthMinValue != null)
                        od.monthMinValue /= 100;
                }
        }
        let response = await this.cvApiService.post(`/api/v1/save_objectives/`, objectiveToSave);
        return response;
    }

    //gets list of user logins for the alert notifications
    async loadUsersList(): Promise<User[]> {
        let response = await this.cvApiService.get<LoginWTO[]>("/api/v1/users/");
        if (response.status_code < 200 || response.status_code >= 300) {
            console.error("Failed to get users.");
            throw response;
        }
        return response.data;
    };

    public async getMetricDefinitions() {
        try {
            let response = await this.cvApiService.get<MetricDefinition[]>("/api/v1/pipeline-config/metric-definitions");
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load metric definitions.');
        }
    }

    /**
     * Returns localized string from the CXOne_Objectives localization section.
     * @param key Key for localized string to retrieve
     * @param defaultValue Value to return if error occurs
     * @param inserts Values to insert into the localized (or default) string; the first will replace {0}, the second will replace {1}, etc.
     */
    public getLocalizationString(key: string, defaultValue: string, ...inserts: string[]) {
        let baseString = this.cvLocalizationFactory.getCachedText("CXOne_Objectives", key, defaultValue);
        for (let i = 0; i < inserts.length; i++) {
            baseString = baseString.replace(`{${i}}`, inserts[i]);
        }
        return baseString;
    }
}
